import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import FreshnessStampView from "@/components/FreshnessStampView";
import { buildFreshnessStampData } from "@/lib/printFreshnessStamp";
import { getOrder } from "@/lib/api";
import { toast } from "sonner";

export default function FreshnessStampPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getOrder(id);
        if (!cancelled) setOrder(res.data);
      } catch {
        if (!cancelled) {
          toast.error("Could not load order");
          navigate(-1);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const stamp = buildFreshnessStampData(order || {});
  const orderNum = order?.order_number;

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#EE2677]" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in mx-auto max-w-md px-4 py-6" data-testid="freshness-stamp-page">
      <style>{`
        @media print {
          .stamp-page-toolbar { display: none !important; }
          .stamp-page-shell {
            padding: 0 !important;
            max-width: none !important;
            border: none !important;
            box-shadow: none !important;
            background: #fff !important;
          }
          .tt-stamp-root, .tt-stamp-root * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .tt-stamp-label {
            box-shadow: none !important;
            border: none !important;
          }
        }
      `}</style>

      <div className="stamp-page-toolbar mb-6 flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="gap-2 text-[#2D2D2D]"
        >
          <ArrowLeft size={18} aria-hidden />
          Back
        </Button>
        <div>
          <h1 className="text-xl font-bold text-[#2D2D2D]">Stamp</h1>
          {orderNum ? (
            <p className="text-sm text-[#666]">Order #{orderNum}</p>
          ) : null}
        </div>
      </div>

      <div className="stamp-page-shell stamp-page-card flex flex-col items-center gap-6 rounded-3xl border border-[#EBB3A9]/40 bg-[#fcf8e3] px-4 py-8">
        <FreshnessStampView stamp={stamp} />

        <p className="stamp-page-toolbar max-w-sm text-center text-xs text-[#666]">
          Full packaging label — stamp plus ingredients. Blank lines until baker and froster mark
          Done. Use by is 5 days after baking (IST).
        </p>

        <Button
          type="button"
          className="stamp-page-toolbar h-12 w-full max-w-xs rounded-full bg-[#EE2677] text-base font-bold text-white hover:bg-[#d91f69]"
          onClick={() => window.print()}
        >
          <Printer className="mr-2 h-5 w-5" aria-hidden />
          Print
        </Button>
      </div>
    </div>
  );
}
